const DEFAULT_NOOP = () => {};

export const DEFAULT_PUBLIC_CAPSULE_AD_POLICY = Object.freeze({
  eligibleViewsPerAd: 5,
  cooldownMs: 15 * 60 * 1000,
  maxAdsPerSession: 2,
  maxAdsPerDay: 3,
  perCapsuleCooldownMs: 7 * 24 * 60 * 60 * 1000,
});

export const AD_POLICY_STORAGE_KEYS = Object.freeze({
  LAST_SHOWN_AT: "inssa:ads:lastShownAt",
  SESSION_SHOWN_COUNT: "inssa:ads:sessionShownCount",
  DAILY_BUCKET_DATE: "inssa:ads:dailyBucketDate",
  DAILY_BUCKET_COUNT: "inssa:ads:dailyBucketCount",
  CAPSULE_LAST_SHOWN_MAP: "inssa:ads:capsuleLastShownMap",
  SESSION_FIRST_ELIGIBLE_SEEN: "inssa:ads:sessionFirstEligibleSeen",
  SESSION_ELIGIBLE_VIEW_COUNT: "inssa:ads:sessionEligibleViewCount",
  SESSION_COUNTED_INSTANCE_KEYS: "inssa:ads:sessionCountedInstanceKeys",
});

const CAPSULE_TYPE_DENYLIST = new Set(["private", "sponsored", "memberexclusive"]);

function toNonNegativeInt(value, fallback = 0) {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

function safeReadJson(readLocal, key, fallback = {}) {
  const raw = readLocal?.(key);
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function safeWriteJson(writeLocal, key, value) {
  try {
    writeLocal?.(key, JSON.stringify(value));
  } catch {
    DEFAULT_NOOP();
  }
}

function getLocalDayBucket(nowMs) {
  const d = new Date(nowMs);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function resolvePolicy(overrides = {}) {
  const merged = {
    ...DEFAULT_PUBLIC_CAPSULE_AD_POLICY,
    ...(overrides && typeof overrides === "object" ? overrides : {}),
  };
  return {
    eligibleViewsPerAd: Math.max(
      1,
      toNonNegativeInt(merged.eligibleViewsPerAd, DEFAULT_PUBLIC_CAPSULE_AD_POLICY.eligibleViewsPerAd)
    ),
    cooldownMs: Math.max(0, toNonNegativeInt(merged.cooldownMs, DEFAULT_PUBLIC_CAPSULE_AD_POLICY.cooldownMs)),
    maxAdsPerSession: Math.max(
      0,
      toNonNegativeInt(merged.maxAdsPerSession, DEFAULT_PUBLIC_CAPSULE_AD_POLICY.maxAdsPerSession)
    ),
    maxAdsPerDay: Math.max(0, toNonNegativeInt(merged.maxAdsPerDay, DEFAULT_PUBLIC_CAPSULE_AD_POLICY.maxAdsPerDay)),
    perCapsuleCooldownMs: Math.max(
      0,
      toNonNegativeInt(
        merged.perCapsuleCooldownMs,
        DEFAULT_PUBLIC_CAPSULE_AD_POLICY.perCapsuleCooldownMs
      )
    ),
  };
}

function countEligibleViewOnce({ instanceKey, readSession, writeSession }) {
  const normalizedInstanceKey = String(instanceKey || "").trim();
  const currentCount = toNonNegativeInt(readSession?.(AD_POLICY_STORAGE_KEYS.SESSION_ELIGIBLE_VIEW_COUNT), 0);

  if (!normalizedInstanceKey) {
    const nextCount = currentCount + 1;
    writeSession?.(AD_POLICY_STORAGE_KEYS.SESSION_ELIGIBLE_VIEW_COUNT, String(nextCount));
    return nextCount;
  }

  const countedInstances = safeReadJson(readSession, AD_POLICY_STORAGE_KEYS.SESSION_COUNTED_INSTANCE_KEYS, {});
  if (countedInstances[normalizedInstanceKey]) {
    return currentCount;
  }

  countedInstances[normalizedInstanceKey] = 1;
  safeWriteJson(writeSession, AD_POLICY_STORAGE_KEYS.SESSION_COUNTED_INSTANCE_KEYS, countedInstances);

  const nextCount = currentCount + 1;
  writeSession?.(AD_POLICY_STORAGE_KEYS.SESSION_ELIGIBLE_VIEW_COUNT, String(nextCount));
  return nextCount;
}

function resetDailyBucketIfNeeded({ now, readLocal, writeLocal }) {
  const dayBucket = getLocalDayBucket(now);
  const storedBucket = String(readLocal?.(AD_POLICY_STORAGE_KEYS.DAILY_BUCKET_DATE) || "");
  if (storedBucket === dayBucket) {
    return {
      dayBucket,
      dayCount: toNonNegativeInt(readLocal?.(AD_POLICY_STORAGE_KEYS.DAILY_BUCKET_COUNT), 0),
    };
  }
  writeLocal?.(AD_POLICY_STORAGE_KEYS.DAILY_BUCKET_DATE, dayBucket);
  writeLocal?.(AD_POLICY_STORAGE_KEYS.DAILY_BUCKET_COUNT, "0");
  return { dayBucket, dayCount: 0 };
}

export function normalizeCapsuleType(capsuleType) {
  return String(capsuleType || "").trim().toLowerCase();
}

export function evaluatePublicCapsuleAdEligibility({
  capsuleType,
  capsuleId,
  instanceKey,
  disabled = false,
  policy,
  now = Date.now(),
  readLocal = DEFAULT_NOOP,
  writeLocal = DEFAULT_NOOP,
  readSession = DEFAULT_NOOP,
  writeSession = DEFAULT_NOOP,
} = {}) {
  const normalizedType = normalizeCapsuleType(capsuleType);
  const resolvedPolicy = resolvePolicy(policy);

  if (disabled) {
    return { canShow: false, reason: "disabled", normalizedType };
  }

  if (CAPSULE_TYPE_DENYLIST.has(normalizedType)) {
    return { canShow: false, reason: "denied_capsule_type", normalizedType };
  }

  if (normalizedType !== "public") {
    return { canShow: false, reason: "non_public_capsule", normalizedType };
  }

  const eligibleViewCount = countEligibleViewOnce({ instanceKey, readSession, writeSession });

  const hasSeenFirstEligible = readSession?.(AD_POLICY_STORAGE_KEYS.SESSION_FIRST_ELIGIBLE_SEEN) === "1";
  if (!hasSeenFirstEligible) {
    writeSession?.(AD_POLICY_STORAGE_KEYS.SESSION_FIRST_ELIGIBLE_SEEN, "1");
    return { canShow: false, reason: "session_grace_first_view", normalizedType };
  }

  if (eligibleViewCount < resolvedPolicy.eligibleViewsPerAd) {
    return { canShow: false, reason: "view_ratio_not_reached", normalizedType };
  }

  const sessionShownCount = toNonNegativeInt(readSession?.(AD_POLICY_STORAGE_KEYS.SESSION_SHOWN_COUNT), 0);
  if (sessionShownCount >= resolvedPolicy.maxAdsPerSession) {
    return { canShow: false, reason: "session_cap_reached", normalizedType };
  }

  const lastShownAt = toNonNegativeInt(readLocal?.(AD_POLICY_STORAGE_KEYS.LAST_SHOWN_AT), 0);
  if (lastShownAt > 0 && now - lastShownAt < resolvedPolicy.cooldownMs) {
    return { canShow: false, reason: "cooldown_active", normalizedType };
  }

  const { dayCount } = resetDailyBucketIfNeeded({ now, readLocal, writeLocal });
  if (dayCount >= resolvedPolicy.maxAdsPerDay) {
    return { canShow: false, reason: "daily_cap_reached", normalizedType };
  }

  const normalizedCapsuleId = String(capsuleId || "").trim();
  if (normalizedCapsuleId) {
    const byCapsule = safeReadJson(readLocal, AD_POLICY_STORAGE_KEYS.CAPSULE_LAST_SHOWN_MAP, {});
    const lastCapsuleShownAt = toNonNegativeInt(byCapsule[normalizedCapsuleId], 0);
    if (lastCapsuleShownAt > 0 && now - lastCapsuleShownAt < resolvedPolicy.perCapsuleCooldownMs) {
      return { canShow: false, reason: "capsule_cap_active", normalizedType };
    }
  }

  return { canShow: true, reason: "eligible", normalizedType };
}

export function recordPublicCapsuleAdShown({
  capsuleId,
  policy,
  now = Date.now(),
  readLocal = DEFAULT_NOOP,
  writeLocal = DEFAULT_NOOP,
  readSession = DEFAULT_NOOP,
  writeSession = DEFAULT_NOOP,
} = {}) {
  const resolvedPolicy = resolvePolicy(policy);
  const timestamp = String(now);
  writeLocal?.(AD_POLICY_STORAGE_KEYS.LAST_SHOWN_AT, timestamp);

  const sessionShownCount = toNonNegativeInt(readSession?.(AD_POLICY_STORAGE_KEYS.SESSION_SHOWN_COUNT), 0);
  writeSession?.(AD_POLICY_STORAGE_KEYS.SESSION_SHOWN_COUNT, String(sessionShownCount + 1));
  writeSession?.(AD_POLICY_STORAGE_KEYS.SESSION_ELIGIBLE_VIEW_COUNT, "0");

  const { dayBucket, dayCount } = resetDailyBucketIfNeeded({ now, readLocal, writeLocal });
  writeLocal?.(AD_POLICY_STORAGE_KEYS.DAILY_BUCKET_DATE, dayBucket);
  writeLocal?.(AD_POLICY_STORAGE_KEYS.DAILY_BUCKET_COUNT, String(dayCount + 1));

  const normalizedCapsuleId = String(capsuleId || "").trim();
  if (!normalizedCapsuleId) return;

  const byCapsule = safeReadJson(readLocal, AD_POLICY_STORAGE_KEYS.CAPSULE_LAST_SHOWN_MAP, {});
  byCapsule[normalizedCapsuleId] = now;

  const pruned = Object.entries(byCapsule).reduce((acc, [key, value]) => {
    const ts = toNonNegativeInt(value, 0);
    if (ts > 0 && now - ts <= resolvedPolicy.perCapsuleCooldownMs) {
      acc[key] = ts;
    }
    return acc;
  }, {});

  safeWriteJson(writeLocal, AD_POLICY_STORAGE_KEYS.CAPSULE_LAST_SHOWN_MAP, pruned);
}
